import {
  CAMPAIGN_DETAILS_STEP,
  TOTAL_CAMPAIGN_WITH_WRITER_STEPS,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.constants";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";

import type {
  CampaignFromTemplateRequest,
  CampaignFromTemplateRequestAudiencesItem,
  CampaignFromTemplateRequestCampaignObjective,
  CampaignFromTemplateRequestProgramType,
  Creatives,
  SelectedCreativeByIndex,
} from "generated/schemas";

const AUDIENCE_SEGMENT_LABELS: Record<string, string> = {
  TRANSFER_PROSPECTS: "Transfer Prospects",
  INTERNATIONAL_PROSPECTS: "International Prospects",
  FIRST_YEAR_PROSPECTS: "First Year Prospects",
  FIRST_YEAR_INQUIRIES: "First Year Inquiries",
  FIRST_YEAR_NEW_NAMES: "First Year New Names",
  TRANSFER_INQUIRIES: "Transfer Inquiries",
  TRANSFER_NEW_NAMES: "Transfer New Names",
  INTERNATIONAL_EXISTING_INQUIRIES: "International Existing Inquiries",
  INTERNATIONAL_NEW_NAMES: "International New Names",
  APPLICANTS: "Applicants",
  ADMITTED_STUDENTS: "Admitted Students",
  DEPOSITED_STUDENTS: "Deposited Students",
  PARENTS: "Parents",
};

const CAMPAIGN_OBJECTIVE_LABELS: Record<string, string> = {
  APPLICATION_GENERATION: "Application Generation",
  DOMAIN_WARMUP: "Domain warmup",
  FAFSA: "FAFSA Submit",
  INQUIRY_CULTIVATION: "Inquiry Cultivation",
  INQUIRY_GENERATION: "Inquiry Generation",
};

export type CampaignTemplateSummary = {
  audienceSegments: string[];
  campaignObjective?: string;
};

export type CampaignWriterDeliverable = {
  id: string;
  index: number;
  channel?: string;
  name?: string;
  populationType: "student" | "parent";
  classes: string[];
};

const POPULATION_KEYS = ["student", "parent"] as const;

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  "web-page": "Web page",
  form: "Form",
};

const parseDeliverableClasses = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string");
};

const parseDeliverableItem = (
  item: unknown,
  index: number,
  populationType: (typeof POPULATION_KEYS)[number],
): CampaignWriterDeliverable => {
  if (!item || typeof item !== "object") {
    return {
      id: `${populationType}-${index}`,
      index,
      populationType,
      classes: [],
    };
  }

  const deliverable = item as Record<string, unknown>;

  return {
    id: `${populationType}-${index}`,
    index,
    channel: typeof deliverable.channel === "string" ? deliverable.channel : undefined,
    name: typeof deliverable.name === "string" ? deliverable.name : undefined,
    populationType,
    classes: parseDeliverableClasses(deliverable.classes),
  };
};

const getPopulationDeliverableItems = (
  deliverables: Record<string, unknown>,
  populationType: (typeof POPULATION_KEYS)[number],
): CampaignWriterDeliverable[] => {
  const populationDeliverables = deliverables[populationType];

  if (!populationDeliverables || typeof populationDeliverables !== "object") {
    return [];
  }

  const items = (populationDeliverables as Record<string, unknown>).items;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item, index) => parseDeliverableItem(item, index, populationType));
};

export const getDeliverablesFromTemplateData = (data: unknown): CampaignWriterDeliverable[] => {
  if (!data || typeof data !== "object") {
    return [];
  }

  const creatives = (data as Record<string, unknown>).creatives;

  if (!creatives || typeof creatives !== "object") {
    return [];
  }

  const creativeRecord = creatives as Record<string, unknown>;

  return POPULATION_KEYS.flatMap((populationType) =>
    getPopulationDeliverableItems(creativeRecord, populationType),
  );
};

export const filterDeliverablesByAllowedChannels = (
  deliverables: CampaignWriterDeliverable[],
  allowedChannels: string[] | undefined,
): CampaignWriterDeliverable[] => {
  if (!allowedChannels) {
    return deliverables;
  }

  const allowedChannelsSet = new Set(allowedChannels);

  return deliverables.filter(
    (deliverable) => !deliverable.channel || allowedChannelsSet.has(deliverable.channel),
  );
};

export const getDeliverableLabel = (deliverable: CampaignWriterDeliverable): string => {
  const trimmedName = deliverable.name?.trim();

  if (trimmedName) {
    return trimmedName;
  }

  if (deliverable.channel) {
    return CHANNEL_LABELS[deliverable.channel] ?? deliverable.channel;
  }

  return "Creative";
};

export const hasSelectedEmailDeliverables = (
  deliverables: CampaignWriterDeliverable[],
  selectedIds: string[],
): boolean => {
  const selectedIdsSet = new Set(selectedIds);

  return deliverables.some(
    (deliverable) => selectedIdsSet.has(deliverable.id) && deliverable.channel === "email",
  );
};

const formatEnumLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const parseAudienceSegments = (summaryRecord: Record<string, unknown>): string[] => {
  if (Array.isArray(summaryRecord.audienceSegments)) {
    return summaryRecord.audienceSegments.filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );
  }

  if (typeof summaryRecord.audienceSegment === "string" && summaryRecord.audienceSegment.length > 0) {
    return [summaryRecord.audienceSegment];
  }

  return [];
};

const extractSummaryFromSection = (
  section: Record<string, unknown>,
): CampaignTemplateSummary => ({
  audienceSegments: parseAudienceSegments(section),
  campaignObjective:
    typeof section.campaignObjective === "string" && section.campaignObjective.length > 0
      ? section.campaignObjective
      : undefined,
});

export const getTemplateSummary = (data: unknown): CampaignTemplateSummary => {
  if (!data || typeof data !== "object") {
    return { audienceSegments: [] };
  }

  const dataRecord = data as Record<string, unknown>;

  const summary = dataRecord.summary;
  if (summary && typeof summary === "object") {
    return extractSummaryFromSection(summary as Record<string, unknown>);
  }

  const campaignDetails = dataRecord["campaign-details"];
  if (campaignDetails && typeof campaignDetails === "object") {
    return extractSummaryFromSection(campaignDetails as Record<string, unknown>);
  }

  return { audienceSegments: [] };
};

export const getTemplateWriterValidationError = (templateData: unknown): string | null => {
  const summary = getTemplateSummary(templateData);

  if (!summary.campaignObjective) {
    return "This campaign template is missing a campaign objective. Edit the template, set it on the Summary tab, save, and try again.";
  }

  return null;
};

export const formatAudienceSegmentsLabel = (audienceSegments: string[]): string =>
  audienceSegments.map((segment) => getAudienceSegmentLabel(segment)).join(", ");

export const getAudienceSegmentLabel = (audienceSegment?: string): string => {
  if (!audienceSegment) {
    return "";
  }

  return AUDIENCE_SEGMENT_LABELS[audienceSegment] ?? formatEnumLabel(audienceSegment);
};

export const getCampaignObjectiveLabel = (campaignObjective?: string): string => {
  if (!campaignObjective) {
    return "";
  }

  return CAMPAIGN_OBJECTIVE_LABELS[campaignObjective] ?? formatEnumLabel(campaignObjective);
};

export const buildDeliverablesPayload = (
  deliverables: CampaignWriterDeliverable[],
  selectedIds: string[],
  templatesByDeliverableId: Record<string, string> = {},
): Creatives => {
  const selectedIdsSet = new Set(selectedIds);
  const student: SelectedCreativeByIndex[] = [];
  const parent: SelectedCreativeByIndex[] = [];

  for (const deliverable of deliverables) {
    if (!selectedIdsSet.has(deliverable.id)) {
      continue;
    }

    const templateTouchpointVersionId = templatesByDeliverableId[deliverable.id];
    const selection: SelectedCreativeByIndex = {
      index: deliverable.index,
      ...(templateTouchpointVersionId && { templateTouchpointVersionId }),
    };

    if (deliverable.populationType === "student") {
      student.push(selection);
    } else {
      parent.push(selection);
    }
  }

  return {
    ...(student.length > 0 && { student }),
    ...(parent.length > 0 && { parent }),
  };
};

export const buildCampaignFromTemplateRequest = (
  formData: CreateCampaignWithWriterFormData,
  templateData: unknown,
): CampaignFromTemplateRequest | null => {
  const templateSummary = getTemplateSummary(templateData);

  if (!templateSummary.campaignObjective) {
    return null;
  }

  const deliverables = getDeliverablesFromTemplateData(templateData);
  const deliverablesPayload = buildDeliverablesPayload(
    deliverables,
    formData.deliverables ?? [],
    formData.deliverableTemplates,
  );
  const hasDeliverableSelections =
    formData.includeCreatives &&
    ((deliverablesPayload.student?.length ?? 0) > 0 || (deliverablesPayload.parent?.length ?? 0) > 0);

  return {
    programType: formData.programStrategy as CampaignFromTemplateRequestProgramType,
    name: formData.campaignName.trim(),
    campaignObjective:
      templateSummary.campaignObjective as CampaignFromTemplateRequestCampaignObjective,
    campaignTemplateId: formData.campaignTemplateId,
    transferStudentStatusFlag: formData.transferStudentStatusFlag ?? false,
    ...(formData.cdnSubdirectoryName && { cdnSubdirectoryName: formData.cdnSubdirectoryName }),
    ...(templateSummary.audienceSegments.length > 0 && {
      audiences: templateSummary.audienceSegments as CampaignFromTemplateRequestAudiencesItem[],
    }),
    ...(hasDeliverableSelections && { creatives: deliverablesPayload }),
    ...(formData.emailAdditionalInstructions?.trim() && {
      emailAdditionalInstructions: formData.emailAdditionalInstructions.trim(),
    }),
  };
};

export const shouldSkipCreativesStep = (
  hasCreatives: boolean,
  includeCreatives: boolean | undefined,
): boolean => !(hasCreatives && Boolean(includeCreatives));

export type DisplayStepInfo = {
  isVisible: boolean;
  displayStep: number;
  totalSteps: number;
};

export const getDisplayStepInfo = (step: number, skipCreativesStep: boolean): DisplayStepInfo => ({
  isVisible: !skipCreativesStep && step > 1,
  displayStep: step - 1,
  totalSteps: TOTAL_CAMPAIGN_WITH_WRITER_STEPS - 1,
});

/** Setup jumps straight to campaign details when there is no creatives step to show. */
export const getNextStep = (step: number, skipCreativesStep: boolean): number =>
  step === 1 && skipCreativesStep ? CAMPAIGN_DETAILS_STEP : step + 1;

/** Campaign details goes back to setup over the skipped creatives step. */
export const getPreviousStep = (step: number, skipCreativesStep: boolean): number =>
  step === CAMPAIGN_DETAILS_STEP && skipCreativesStep ? 1 : step - 1;
